import { Icon } from "@iconify/vue2";
import VueSlider from "vue-slider-component";
import "vue-slider-component/theme/default.css";
import { parseVideoSource } from "@/utils/videoSource";
import {
    createPlaybackLink,
    createDirectPlaybackLink,
} from "@/components/video-providers/r2/playbackLink";
import {
    formatTime,
    parseResumeTime,
    resumeKey,
    mapSubtitleTracks,
    mapQualityLevels,
} from "./helpers";
// The INTERCEPTED axios instance (bearer / Session-Id / locale / single-flight
// refresh). NEVER `this.$http`: that is vue-axios's raw axios — the utils/https
// install-plugin is never registered in main.js, so prototype `$http` carries
// no auth headers and the BFF answers 401 (bitten in prod, 2026-07-30). Same
// warning lives in video-providers/r2/index.js.
import { $http } from "@/utils/https";

// Platform-look R2 player. The UI is a byte-for-byte reuse of the Vimeo player's
// custom skin (video-player/vimeo/* + the shared index.scss); the playback
// engine is native <video> + hls.js, wired exactly like the battle-tested Plyr
// provider (video-providers/r2/index.js): hls.js-FIRST (canPlayType is
// spoofable), native fallback only for MSE-less engines (iPhone Safari), plain
// mp4 -> video.src; link expiry / one-silent-retry / single-flight / invalidate.
//
// Subtitles and quality reuse the donor's exact menu markup, adapted to native
// media (mirrors profile-new's createHtml5Adapter):
//   - Subtitles: side-loaded VTT <track> elements built imperatively from the
//     play payload's `textTracks` ([{ lang, label, src }]). <track> works for
//     BOTH hls.js and mp4, so we deliberately do NOT use hls.js's own subtitle
//     API. Default off; the CC menu flips textTracks[].mode (showing/hidden).
//     Rebuilt with fresh signed URLs on every link refresh, selection preserved.
//   - Quality: hls.js levels (label `${height}p`) + «Авто» (currentLevel = -1,
//     default). Hidden for mp4 / native-HLS fallback (no MSE level control).
//     The choice persists across refreshes (re-applied on MANIFEST_PARSED).

// Inline i18n fallbacks — vue-profile ships NO local locale files (translations
// are fetched from the CMS at runtime), so a $t key the CMS has not published
// yet renders as the raw key. Mirrors video-providers/r2/index.js so the error
// UI reads correctly in both locales until the CMS adds these keys.
const I18N_FALLBACK = {
    videoUnavailable: { en: "Video is unavailable", uk: "Відео недоступне" },
    videoTryAgain: { en: "Try again", uk: "Спробувати знову" },
};

export default {
    name: "video-player-r2",
    components: { Icon, VueSlider },
    props: {
        videoUrl: {
            type: String,
            required: true,
        },
        // Course provider (`r2`) — used when src is a signed URL that is not
        // the canonical watch/ form (play src directly, skip BFF).
        videoProvider: {
            type: String,
            required: false,
            default: null,
        },
        // LMS material/video id — the resume key (`_dmvideo_<id>`). Optional:
        // consumers that don't pass it just lose resume / the continue overlay.
        videoId: {
            type: [String, Number],
            required: false,
            default: null,
        },
        isKeepInViewportMode: {
            type: Boolean,
            required: false,
            default: false,
        },
    },
    data() {
        return {
            isLoading: true,
            isPlaying: false,
            isPlayingIndicatorActive: false,
            isSeeking: false,
            isMoveActive: false,
            isMoveBackward: false,
            isMoveForward: false,
            isVideoStarted: false,
            isInViewport: true,
            observer: null,
            loadError: false,
            cookieData: undefined,
            thumbnailUrl: "",
            volume: 1,
            isFullscreen: false,
            fullscreenWidth: window.innerWidth,
            fullscreenHeight: window.innerHeight,
            currentTime: 0,
            totalTime: 0,
            // Quality menu: «Авто» + one label per hls.js rendition height.
            // Populated on MANIFEST_PARSED for the hls.js branch; empty (button
            // hidden) for mp4 / native-HLS fallback.
            availableQualities: [],
            selectedQuality: "",
            availableSpeeds: [0.5, 0.75, 1, 1.1, 1.25, 1.37, 1.5, 1.75, 2],
            selectedSpeed: 1,
            isSpaceHeld: false,
            spaceHoldTimer: null,
            previousSpeed: 1,
            touchHoldTimer: null,
            isTouchHolding: false,
            mouseHoldTimer: null,
            isMouseHolding: false,
            skipNextClick: false,
            lastRightTapTime: 0,
            singleTapTimer: null,
            lastLeftTapTime: 0,
            singleLeftTapTimer: null,
            navigatorHideTime: 2000,
            isNavigationActive: false,
            navigationTimer: null,
            moveTimer: null,
            isVolumeChanging: false,
            isVolumeActive: false,
            volumeChangingTimer: null,
            // Subtitle menu list ([{ language, label, src? }] incl. an "off"
            // sentinel), built from the play payload's side-loaded VTT tracks.
            // Empty (button hidden) when the lesson carries no subtitles.
            // `selectedSubtitle` is the active language ("" = off, the default).
            subtitleTracks: [],
            selectedSubtitle: "",
        };
    },
    computed: {
        formattedCurrentTime() {
            return this.formatTime(this.currentTime);
        },
        formattedTotalTime() {
            return this.formatTime(this.totalTime);
        },
        formattedContinueTime() {
            if (!this.cookieData || !this.cookieData.currentTime) return "";
            return this.formatTime(this.cookieData.currentTime);
        },
        isIos() {
            return /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
        },
        isWindows() {
            return /Windows/i.test(navigator.userAgent);
        },
        isAndroid() {
            return /Android/i.test(navigator.userAgent);
        },
        fullscreenStyle() {
            return {
                width: `${this.fullscreenWidth}px`,
                height: `${this.fullscreenHeight}px`,
            };
        },
        isMobile() {
            return window.innerWidth > 980;
        },
        loadErrorMessage() {
            return this.tr("videoUnavailable");
        },
        retryText() {
            return this.tr("videoTryAgain");
        },
    },
    created() {
        // Non-reactive playback machinery (deliberately off `data` — imperative
        // handles that must never trigger re-renders). Mirrors the Plyr provider.
        //
        // Two sources:
        //   - watch URL → BFF play link (lessonId)
        //   - course signed R2 mp4 → play src directly (no BFF)
        const parsed = parseVideoSource(this.videoUrl);
        const providerIsR2 =
            String(this.videoProvider || "").toLowerCase() === "r2";
        this._lessonId = parsed.kind === "r2" ? parsed.lessonId : null;
        this._directSrc =
            parsed.kind === "r2-direct"
                ? parsed.src
                : providerIsR2 && this.videoUrl && !this._lessonId
                  ? String(this.videoUrl)
                  : null;
        this._link = this._directSrc
            ? createDirectPlaybackLink(this._directSrc)
            : createPlaybackLink({
                  lessonId: this._lessonId,
                  http: $http,
                  // Stable BFF convention (same spirit as the R2_WATCH_BASE literal) —
                  // baked in so builds work with zero CI variables;
                  // VUE_APP_VIDEO_PLAY_BFF overrides for local dev.
                  baseUrl: process.env.VUE_APP_VIDEO_PLAY_BFF || "https://edu-new.genius.space",
              });
        this._hls = null;
        this._destroyed = false;
        this._recovered = false; // one-silent-retry latch; re-armed by progress
        this._pendingRestore = null; // {time, wasPlaying} applied on next loadedmetadata
        this._media = null;
        this._listeners = [];
        this._subtitleTrackEls = []; // side-loaded <track> nodes (rebuilt on refresh)
        this._qualityByLabel = {}; // menu label -> hls level index (Auto -> -1)
    },
    mounted() {
        const media = this.$refs.video;
        this._media = media;

        if (!this._lessonId && !this._directSrc) {
            // Guard so a malformed URL surfaces the error screen, not a hang.
            // eslint-disable-next-line no-console
            console.error("Unrecognized R2 playback URL:", this.videoUrl);
            this.loadError = true;
            this.isLoading = false;
        } else if (media) {
            this.bindMediaEvents(media);
            this.loadInitial();
        }

        // Continue-watching prompt: read the resume position persisted under the
        // LMS material id (`_dmvideo_<videoId>`), same convention as the Plyr
        // provider so a lesson resumes across either player.
        if (this.videoId != null) {
            const resume = parseResumeTime(localStorage.getItem(resumeKey(this.videoId)));
            if (resume) this.cookieData = { currentTime: resume };
        }

        window.addEventListener("keydown", this.handleKeyPress);
        window.addEventListener("keyup", this.handleKeyUp);
        document.addEventListener("fullscreenchange", this.handleFullscreenChange);
        document.addEventListener("webkitfullscreenchange", this.handleFullscreenChange);

        this.observer = new IntersectionObserver(
            ([entry]) => {
                this.isInViewport = entry.isIntersecting;
            },
            { threshold: 0.1 }
        );
        this.observer.observe(this.$el);

        this.updateFullscreenSize();
        window.addEventListener("resize", this.updateFullscreenSize);
        window.addEventListener("orientationchange", this.updateFullscreenSize);
    },
    beforeDestroy() {
        // Clean teardown: hls destroyed, listeners removed, link invalidated so
        // any in-flight refresh resolves to null instead of touching a dead
        // element.
        this._destroyed = true;
        this._link.invalidate();
        if (this._hls) {
            this._hls.destroy();
            this._hls = null;
        }
        if (this._media) {
            for (const [event, handler] of this._listeners) {
                this._media.removeEventListener(event, handler);
            }
        }

        window.removeEventListener("keydown", this.handleKeyPress);
        window.removeEventListener("keyup", this.handleKeyUp);
        document.removeEventListener("fullscreenchange", this.handleFullscreenChange);
        document.removeEventListener("webkitfullscreenchange", this.handleFullscreenChange);

        if (this.observer) this.observer.disconnect();

        window.removeEventListener("resize", this.updateFullscreenSize);
        window.removeEventListener("orientationchange", this.updateFullscreenSize);
    },
    watch: {
        volume() {
            this.changeVolume();
        },
        selectedSpeed() {
            this.changeSpeed();
        },
        selectedSubtitle() {
            this.applySubtitleMode();
        },
        selectedQuality() {
            this.changeQuality();
        },
    },
    methods: {
        tr(key) {
            const value = this.$t(key);
            if (value && value !== key) return value;
            const lang =
                (this.$store &&
                    this.$store.getters.currentLanguage &&
                    this.$store.getters.currentLanguage.translationKey) ||
                "uk";
            const fallback = I18N_FALLBACK[key] || {};
            return fallback[lang] || fallback.uk || key;
        },

        // ---- Backend wiring (mirrors video-providers/r2/index.js) --------------

        bindMediaEvents(media) {
            const on = (event, handler) => {
                media.addEventListener(event, handler);
                this._listeners.push([event, handler]);
            };

            on("loadedmetadata", () => {
                if (this._destroyed) return;
                if (isFinite(media.duration)) this.totalTime = Math.round(media.duration);
                this.isLoading = false;
            });
            // Some engines fire canplay before a usable loadedmetadata — hide the
            // loader either way.
            on("canplay", () => {
                if (this._destroyed) return;
                this.isLoading = false;
            });

            on("timeupdate", () => {
                if (this._destroyed) return;
                if (!this.isSeeking) {
                    this.currentTime = media.currentTime;
                    if (isFinite(media.duration)) this.totalTime = Math.round(media.duration);
                }
                if (media.currentTime > 0) this._recovered = false; // progress re-arms retry
                // Persist resume under the LMS material id (see helpers.resumeKey).
                if (this.videoId != null) {
                    localStorage.setItem(resumeKey(this.videoId), String(media.currentTime));
                }
            });

            on("play", () => {
                this.isPlaying = true;
                // Proactive refresh: a play past expiresAt-10s must never hang on
                // a dead URL. Overlapping triggers coalesce (single-flight).
                if (!this._destroyed && this._link.isExpired()) this.refreshLink();
            });
            on("pause", () => {
                this.isPlaying = false;
            });
            on("seeking", () => {
                if (!this._destroyed && this._link.isExpired()) this.refreshLink();
            });
            on("ended", () => {
                media.currentTime = 0;
                this.isPlaying = false;
            });

            // Media <video> error -> ONE silent retry; a second consecutive
            // error with no progress surfaces the error screen.
            on("error", () => {
                if (this._destroyed || !media.src) return;
                this.onFatal();
            });
        },

        async loadInitial() {
            this.isLoading = true;
            this.loadError = false;
            try {
                const payload = await this._link.load();
                if (this._destroyed) return;
                await this.applyLink(payload);
            } catch (e) {
                if (this._destroyed) return;
                this.showError(e);
            }
        },

        // Kind-aware source binding. hls.js (MSE) is tried FIRST for any 'hls'
        // link — canPlayType is spoofable (extensions fake native HLS then fail
        // to play a master.m3u8 as video.src). Native playback is only a
        // fallback for engines without MSE (iPhone Safari); plain mp4 assigns
        // video.src directly. Poster is (re)applied on every load so a stale one
        // never survives a link change.
        async applyLink(payload) {
            const media = this._media;
            if (!media || !payload) return;

            if (payload.poster) {
                media.poster = payload.poster;
                this.thumbnailUrl = payload.poster;
            } else {
                media.removeAttribute("poster");
                this.thumbnailUrl = "";
            }

            // Side-load VTT subtitles + set crossorigin BEFORE any src assignment
            // below, so a lesson with tracks loads its <video> in the CORS-aware
            // mode from the start (mirrors createHtml5Adapter ordering).
            this.rebuildSubtitleTracks(payload.textTracks);

            if (payload.kind === "hls") {
                const epoch = this._link.epoch;
                const HlsMod = (await import("hls.js")).default;
                // The dynamic import is the only await here — re-check after it:
                // destroy() or a fatal error may have landed while the chunk
                // loaded (else: an orphaned hls instance / a resume behind the
                // error UI).
                if (this._destroyed || epoch !== this._link.epoch) return;
                if (HlsMod.isSupported()) {
                    if (!this._hls) {
                        this._hls = new HlsMod();
                        this._hls.on(HlsMod.Events.ERROR, (_evt, errData) => {
                            if (this._destroyed || !errData || !errData.fatal) return;
                            this.onFatal();
                        });
                        // Rebuild the quality menu once the rendition ladder is
                        // known; fires again on every loadSource (refresh), so the
                        // user's pick is re-applied to the fresh levels.
                        this._hls.on(HlsMod.Events.MANIFEST_PARSED, () => {
                            if (this._destroyed) return;
                            this.rebuildQuality();
                        });
                        this._hls.attachMedia(media);
                    }
                    this._hls.loadSource(payload.src);
                    return;
                }
            }

            // mp4, or hls on a native-HLS engine without MSE: no MSE level
            // control, so hide the quality menu (single rendition).
            if (this._hls) {
                this._hls.destroy();
                this._hls = null;
            }
            this.availableQualities = [];
            this.selectedQuality = "";
            media.src = payload.src;
        },

        async refreshLink() {
            const media = this._media;
            if (!media) return;
            // Snapshot restore target BEFORE the async fetch swaps the source.
            const restore = { time: media.currentTime || 0, wasPlaying: !media.paused };
            try {
                const payload = await this._link.refresh();
                if (this._destroyed || !payload) return; // null == invalidated
                this._pendingRestore = restore;
                await this.applyLink(payload);
                if (this._destroyed) return;
                this.applyPendingRestore();
            } catch (e) {
                if (this._destroyed) return;
                this.showError(e);
            }
        },

        applyPendingRestore() {
            const media = this._media;
            const restore = this._pendingRestore;
            if (!media || !restore) return;

            const apply = () => {
                media.removeEventListener("loadedmetadata", apply);
                if (this._destroyed) return;
                try {
                    media.currentTime = restore.time;
                } catch (e) {
                    // seeking before metadata is ready — ignore, timeupdate self-heals
                }
                if (restore.wasPlaying) {
                    const p = media.play();
                    if (p && p.catch) p.catch(() => {});
                }
                this._pendingRestore = null;
            };

            if (media.readyState >= 1) apply();
            else media.addEventListener("loadedmetadata", apply);
        },

        // Media <video> error OR hls.js fatal error: one transparent retry, then
        // give up. A second consecutive failure with no progress in between
        // (recovered still true) invalidates any in-flight refresh and surfaces
        // the error screen — nothing may resume behind it.
        onFatal() {
            if (this._recovered) {
                this._link.invalidate();
                this.showError();
                return;
            }
            this._recovered = true;
            this.refreshLink();
        },

        showError(e) {
            if (e && e.message) {
                // eslint-disable-next-line no-console
                console.error("[video-player-r2] play link error:", e.message);
            }
            this.isLoading = false;
            this.loadError = true;
        },

        retry() {
            this._recovered = false;
            this.loadInitial();
        },

        // ---- Subtitles (side-loaded VTT <track>, hls + mp4) --------------------

        // Rebuild the <track> elements (and the CC menu) from the payload's
        // textTracks. Called on initial load and every refresh — old nodes are
        // dropped and recreated with the fresh signed URLs. crossorigin is set
        // ONLY when there is at least one track, so subtitle-less lessons keep
        // their mp4 requests in the default (no-CORS) mode. Mirrors
        // createHtml5Adapter.rebuildSubtitleTracks.
        rebuildSubtitleTracks(payloadTracks) {
            const media = this._media;
            for (const el of this._subtitleTrackEls) el.remove();
            this._subtitleTrackEls = [];

            const menu = mapSubtitleTracks(payloadTracks); // real + off, or []
            this.subtitleTracks = menu;
            const real = menu.filter((t) => t.src);

            // Preserve the user's selection across a refresh only while it still
            // exists in the fresh track set.
            if (
                this.selectedSubtitle &&
                !real.some((t) => t.language === this.selectedSubtitle)
            ) {
                this.selectedSubtitle = "";
            }

            if (!media) return;
            if (real.length) media.setAttribute("crossorigin", "anonymous");
            else media.removeAttribute("crossorigin");

            for (const t of real) {
                const el = document.createElement("track");
                el.setAttribute("kind", "subtitles");
                el.setAttribute("srclang", t.language);
                if (t.label) el.setAttribute("label", t.label);
                el.setAttribute("src", t.src);
                media.appendChild(el);
                this._subtitleTrackEls.push(el);
            }

            this.applySubtitleMode();
        },

        // Flip native textTracks[].mode to reflect the selection: the track whose
        // language matches `selectedSubtitle` shows, all others hide (off = "" =>
        // everything hidden). Fresh <track> nodes start hidden, so this also
        // re-asserts a preserved selection after a refresh.
        applySubtitleMode() {
            const media = this._media;
            if (!media || !media.textTracks) return;
            const tracks = media.textTracks;
            for (let i = 0; i < tracks.length; i++) {
                const tt = tracks[i];
                tt.mode =
                    tt.language && tt.language === this.selectedSubtitle
                        ? "showing"
                        : "hidden";
            }
        },

        // ---- Quality (hls.js levels) ------------------------------------------

        // Rebuild the quality menu from the parsed rendition ladder and re-apply
        // the persisted pick (by label, since the label survives re-indexing).
        // Called on MANIFEST_PARSED, i.e. after every loadSource.
        rebuildQuality() {
            if (!this._hls) return;
            const mapped = mapQualityLevels(this._hls.levels);
            this._qualityByLabel = {};
            for (const m of mapped) this._qualityByLabel[m.label] = m.level;
            // A ladder with only «Авто» (no real heights) offers no choice — keep
            // the menu hidden in that case.
            this.availableQualities = mapped.length > 1 ? mapped.map((m) => m.label) : [];

            if (this.availableQualities.indexOf(this.selectedQuality) === -1) {
                this.selectedQuality = this.availableQualities.length
                    ? this.availableQualities[0] // «Авто»
                    : "";
            } else {
                this.changeQuality();
            }
        },

        changeQuality() {
            if (!this._hls) return;
            const level = this._qualityByLabel[this.selectedQuality];
            this._hls.currentLevel = level === undefined ? -1 : level;
        },

        // ---- Playback controls (ported from the Vimeo donor, native calls) -----

        timelineMove(value) {
            if (this.isMoveActive || !this.isPlaying) return;
            this._media.currentTime = this.currentTime + value;
            this.isMoveActive = true;
            value > 0 ? (this.isMoveForward = true) : (this.isMoveBackward = true);
            setTimeout(() => {
                this.isMoveActive = false;
                value > 0
                    ? (this.isMoveForward = false)
                    : (this.isMoveBackward = false);
            }, 300);
        },
        moveForward() {
            this.timelineMove(10);
        },
        moveBackward() {
            this.timelineMove(-10);
        },
        indicatorMoveForward() {
            clearTimeout(this.moveTimer);
            if (!this.isPlaying) return this.playVideo();
            this.moveForward();
        },
        indicatorMoveBackward() {
            clearTimeout(this.moveTimer);
            if (!this.isPlaying) return this.playVideo();
            this.moveBackward();
        },
        handleArrowVolumeChange(strategy) {
            this.isVolumeChanging = true;
            clearTimeout(this.volumeChangingTimer);
            this.volumeChangingTimer = setTimeout(() => {
                this.isVolumeChanging = false;
            }, this.navigatorHideTime);

            if (strategy === "increment") {
                const newVolume = this.volume + 0.1;
                this.volume = newVolume > 1 ? 1 : newVolume;
            } else if (strategy === "decrement") {
                const newVolume = this.volume - 0.1;
                this.volume = newVolume < 0 ? 0 : newVolume;
            }
        },
        isEditableFocused() {
            const el = document.activeElement;
            if (!el) return false;
            const tag = el.tagName;
            return tag === "INPUT" || tag === "TEXTAREA" || el.contentEditable === "true";
        },
        handleKeyPress(event) {
            if (this.isEditableFocused()) return;

            if (event.code === "Space" && this.isInViewport && !event.repeat) {
                event.preventDefault();
                this.spaceHoldTimer = setTimeout(() => {
                    this.isSpaceHeld = true;
                    this.enableTemp2xSpeed();
                }, 300);
            } else if (event.code === "Space" && event.repeat) {
                event.preventDefault();
            }

            if (event.code === "Escape") {
                this.isFullscreen = false;
                document.body.classList.remove("no-scroll");
            }

            if (event.code === "ArrowRight") {
                event.preventDefault();
                this.moveForward();
            } else if (event.code === "ArrowLeft") {
                event.preventDefault();
                this.moveBackward();
            }

            if (event.code === "ArrowUp") {
                event.preventDefault();
                this.handleArrowVolumeChange("increment");
            } else if (event.code === "ArrowDown") {
                event.preventDefault();
                this.handleArrowVolumeChange("decrement");
            }
        },
        handleKeyUp(event) {
            if (this.isEditableFocused()) return;
            if (event.code === "Space") {
                event.preventDefault();
                clearTimeout(this.spaceHoldTimer);
                if (this.isSpaceHeld) {
                    this.isSpaceHeld = false;
                    this.disableTemp2xSpeed();
                } else {
                    this.toggleVideo();
                }
            }
        },
        showPlayIndicator() {
            this.isPlayingIndicatorActive = true;
            setTimeout(() => {
                this.isPlayingIndicatorActive = false;
            }, 300);
        },
        async playVideo(startTime) {
            const media = this._media;
            if (!media || this.isPlaying) return;

            if (!this.isVideoStarted && typeof startTime === "number" && isFinite(startTime)) {
                try {
                    media.currentTime = startTime;
                } catch (e) {
                    // metadata not ready — ignore, the seek self-heals
                }
            }

            try {
                media.muted = false;
                media.volume = this.volume || 1;
                const p = media.play();
                if (p && p.then) await p;
                this.isVideoStarted = true;
                this.showPlayIndicator();
            } catch (e) {
                // eslint-disable-next-line no-console
                console.error("Video play error:", e);
            }
        },
        pauseVideo() {
            const media = this._media;
            if (!media || !this.isPlaying) return;
            media.pause();
            this.showPlayIndicator();
        },
        toggleVideo() {
            clearTimeout(this.moveTimer);
            this.moveTimer = setTimeout(() => {
                this.isPlaying ? this.pauseVideo() : this.playVideo();
            }, 200);
        },
        continueVideo() {
            const t =
                this.cookieData && this.cookieData.currentTime
                    ? this.cookieData.currentTime
                    : 0;
            this.playVideo(t);
        },
        toggleVolume() {
            this.volume = this.volume === 0 ? 1 : 0;
        },
        toggleVolumeModal() {
            this.isVolumeActive = !this.isVolumeActive;
        },
        hideVolumeModal() {
            this.isVolumeActive = false;
        },
        goFullscreen() {
            const videoElement = this.$refs.videoContainer;
            if (videoElement.requestFullscreen) {
                videoElement.requestFullscreen();
            } else if (videoElement.mozRequestFullScreen) {
                videoElement.mozRequestFullScreen();
            } else if (videoElement.webkitRequestFullscreen) {
                videoElement.webkitRequestFullscreen();
            } else if (videoElement.msRequestFullscreen) {
                videoElement.msRequestFullscreen();
            }
        },
        exitFullscreen() {
            if (document.exitFullscreen) {
                document.exitFullscreen();
            } else if (document.mozCancelFullScreen) {
                document.mozCancelFullScreen();
            } else if (document.webkitExitFullscreen) {
                document.webkitExitFullscreen();
            } else if (document.msExitFullscreen) {
                document.msExitFullscreen();
            }
        },
        toggleFullscreen() {
            // iOS Safari only supports fullscreen on the media element itself
            // (the container path is a no-op there). This is the native
            // equivalent of the donor's Vimeo `player.requestFullscreen()`.
            if (this.isIos) {
                const media = this._media;
                if (media && media.webkitEnterFullscreen) media.webkitEnterFullscreen();
                return;
            }

            if (document.fullscreenElement || document.webkitFullscreenElement) {
                this.exitFullscreen();
            } else {
                this.goFullscreen();
            }
        },
        handleFullscreenChange() {
            // The donor never syncs isFullscreen back (its icon/style never
            // flip); native fullscreen fires a real event, so wire it up.
            this.isFullscreen = !!(document.fullscreenElement || document.webkitFullscreenElement);
        },
        updateFullscreenSize() {
            setTimeout(() => {
                this.fullscreenWidth = window.innerWidth;
                this.fullscreenHeight = window.innerHeight;
            }, 100);
        },
        changeVolume() {
            if (this._media) this._media.volume = this.volume;
        },
        changeSpeed() {
            if (this._media) this._media.playbackRate = this.selectedSpeed;
        },
        startSeeking() {
            this.isSeeking = true;
        },
        changeProgress() {
            const media = this._media;
            if (media) media.currentTime = this.currentTime;
            this.isSeeking = false;
        },
        formatTime(seconds) {
            return formatTime(seconds);
        },
        handleLeftTouchStart(event) {
            event.preventDefault();
        },
        handleLeftTouchEnd() {
            const now = Date.now();
            if (now - this.lastLeftTapTime < 300) {
                clearTimeout(this.singleLeftTapTimer);
                this.lastLeftTapTime = 0;
                this.indicatorMoveBackward();
            } else {
                this.singleLeftTapTimer = setTimeout(() => {
                    this.toggleVideo();
                }, 250);
            }
            this.lastLeftTapTime = now;
        },
        handleRightTouchStart(event) {
            event.preventDefault();
            this.touchHoldTimer = setTimeout(() => {
                this.isTouchHolding = true;
                this.enableTemp2xSpeed();
            }, 300);
        },
        handleRightTouchEnd() {
            clearTimeout(this.touchHoldTimer);
            if (this.isTouchHolding) {
                this.isTouchHolding = false;
                this.disableTemp2xSpeed();
                return;
            }
            const now = Date.now();
            if (now - this.lastRightTapTime < 300) {
                clearTimeout(this.singleTapTimer);
                this.lastRightTapTime = 0;
                this.indicatorMoveForward();
            } else {
                this.singleTapTimer = setTimeout(() => {
                    this.toggleVideo();
                }, 250);
            }
            this.lastRightTapTime = now;
        },
        handleRightMouseDown(event) {
            if (event.button !== 0) return;
            this.mouseHoldTimer = setTimeout(() => {
                this.isMouseHolding = true;
                this.enableTemp2xSpeed();
            }, 300);
        },
        handleRightMouseUp() {
            clearTimeout(this.mouseHoldTimer);
            if (this.isMouseHolding) {
                this.isMouseHolding = false;
                this.skipNextClick = true;
                this.disableTemp2xSpeed();
            }
        },
        handleRightClick() {
            if (this.skipNextClick) {
                this.skipNextClick = false;
                return;
            }
            this.toggleVideo();
        },
        enableTemp2xSpeed() {
            if (this.selectedSpeed !== 2) {
                this.previousSpeed = this.selectedSpeed;
                this.selectedSpeed = 2;
            }
        },
        disableTemp2xSpeed() {
            if (this.previousSpeed) {
                this.selectedSpeed = this.previousSpeed;
            }
        },
        handleNavigationShow() {
            if (this.navigationTimer) clearTimeout(this.navigationTimer);

            this.isNavigationActive = true;
            this.navigationTimer = setTimeout(() => {
                this.isNavigationActive = false;
            }, this.navigatorHideTime);
        },
        async togglePiP() {
            // Native PiP — the faithful equivalent of the donor's Vimeo
            // requestPictureInPicture (NOT an invented feature).
            const media = this._media;
            if (!media || !media.requestPictureInPicture) return;
            try {
                if (document.pictureInPictureElement) {
                    await document.exitPictureInPicture();
                } else {
                    await media.requestPictureInPicture();
                }
            } catch (error) {
                // eslint-disable-next-line no-console
                console.error("Error PiP:", error);
            }
        },
    },
};
